import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Platform } from '../utils/platform';
import { useBottomSheet } from '../hooks/useBottomSheet';
import CategoryIcon from '../CategoryIcon.jsx';
import AuthManager from '../utils/authManager';
import botAPI from '../botAPI';
import {
  MapPin,
  Anchor,
  Fish,
  Snowflake,
  Flame,
  Calendar,
  Zap,
  Ship,
  Truck,
  Award,
  Package,
  Thermometer,
  Users,
  Share2,
  Plus,
  Minus,
  X,
} from 'lucide-react';
import '../styles/ProductDetailsModal.css';

/**
 * Склонение единиц измерения по числу (русский язык)
 * @param {number} n - число
 * @param {string} unit - единица измерения из БД (кг, банка, шт, упак)
 * @returns {string} - склонённая форма (именительный/родительный падеж)
 */
const pluralizeUnit = (n, unit) => {
  const num = Math.abs(n);
  // [именительный ед., родительный ед., родительный мн.]
  // 1 банка, 2 банки, 5 банок
  const forms = {
    'кг': ['кг', 'кг', 'кг'],
    'банка': ['банка', 'банки', 'банок'],
    'шт': ['шт', 'шт', 'шт'],
    'упак': ['упаковка', 'упаковки', 'упаковок'],
    'л': ['л', 'л', 'л'],
    'г': ['г', 'г', 'г'],
  };

  const unitForms = forms[unit] || [unit, unit, unit];

  const lastTwo = num % 100;
  const lastOne = num % 10;

  if (lastTwo >= 11 && lastTwo <= 19) {
    return unitForms[2]; // банок
  }
  if (lastOne === 1) {
    return unitForms[0]; // банка
  }
  if (lastOne >= 2 && lastOne <= 4) {
    return unitForms[1]; // банки
  }
  return unitForms[2]; // банок
};

/**
 * Заголовок секции "Путь рыбы" по категории товара
 * Fallback "Путь этого товара" для неизвестных категорий
 */
const getJourneyTitle = (categoryName) => {
  const titles = {
    'Креветки и лангустины': 'Путь этой креветки',
    'Моллюски': 'Путь этого моллюска',
    'Свежая рыба': 'Путь этой рыбы',
    'Соленая и копченая рыба': 'Путь этой рыбы',
    'Красная икра': 'Путь этой икры',
    'Консервы': 'Путь этого товара',
  };
  return titles[categoryName] || 'Путь этого товара';
};

/**
 * Винительный падеж единицы измерения (для "за ...")
 * "за банку", "за упаковку", "за кг", "за шт"
 */
const accusativeUnit = (unit) => {
  const accusative = {
    'кг': 'кг',
    'банка': 'банку',
    'шт': 'шт',
    'упак': 'упаковку',
    'л': 'л',
    'г': 'г',
  };
  return accusative[unit] || unit;
};

/**
 * PackShareSection - Секция "Совместная покупка" для оптовых упаковок
 * Показывается только если у товара есть wholesale_pack_size
 */
const PackShareSection = ({ product, pluralizeUnit }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [desiredQty, setDesiredQty] = useState(product?.min_order_quantity || 1);
  const [isJoining, setIsJoining] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const packSize = product?.wholesale_pack_size;
  const cycleProductId = product?.cycle_product_id;

  // Fetch share progress when expanded
  const fetchProgress = useCallback(async () => {
    if (!cycleProductId) return;
    try {
      const data = await botAPI.packShareProgress(cycleProductId);
      setShareData(data);
      if (data.user_interest) {
        setDesiredQty(data.user_interest);
      }
    } catch (err) {
      // Ignore — progress is optional
    }
  }, [cycleProductId]);

  useEffect(() => {
    if (isExpanded && cycleProductId) {
      fetchProgress();
    }
  }, [isExpanded, cycleProductId, fetchProgress]);

  // Also auto-expand if product already has share interests
  useEffect(() => {
    if (product?.share_interest_count > 0 && packSize > 0) {
      setIsExpanded(true);
    }
  }, [product?.share_interest_count, packSize]);

  if (!packSize || packSize <= 0 || !cycleProductId) return null;

  const [showLoginHint, setShowLoginHint] = useState(false);

  const handleJoin = async () => {
    const token = AuthManager.getAccessToken();
    if (!token) {
      Platform.hapticFeedback('error');
      if (Platform.isStandalone()) {
        // В браузере — перенаправляем на страницу входа
        window.location.href = '/login';
      } else {
        // В Telegram/Max — показываем подсказку внутри секции
        setShowLoginHint(true);
      }
      return;
    }

    setIsJoining(true);
    Platform.hapticFeedback('light');
    try {
      const data = await botAPI.packShareJoin(cycleProductId, desiredQty);
      setShareData(data);
      setShowLoginHint(false);
      Platform.hapticFeedback('success');
    } catch (err) {
      alert(err.message || 'Ошибка');
    } finally {
      setIsJoining(false);
    }
  };

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      await botAPI.packShareCancel(cycleProductId);
      await fetchProgress();
      Platform.hapticFeedback('light');
    } catch (err) {
      // Ignore
    } finally {
      setIsCancelling(false);
    }
  };

  const handleShare = async () => {
    const productId = product?.product_id || product?.id;
    const refCode = (() => { try { return sessionStorage.getItem('user_referral_code'); } catch { return null; } })();
    let shareUrl = `${window.location.origin}/product/${productId}?share=1`;
    if (refCode) shareUrl += `&ref=${refCode}`;
    const shareText = `Давай купим вместе! ${product.name || product.product_name} — осталось ${shareData?.remaining || '?'} ${pluralizeUnit(shareData?.remaining || 0, product.unit || 'кг')} до полной упаковки`;

    Platform.hapticFeedback('light');

    if (navigator.share) {
      try {
        await navigator.share({ title: 'Совместная покупка', text: shareText, url: shareUrl });
      } catch {
        // User cancelled share
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Ссылка скопирована!');
      } catch {
        // Fallback
      }
    }
  };

  const stepQty = product.step_quantity || 1;
  const unit = product.unit || 'кг';
  const bonusPercent = product.wholesale_bonus_percent || 3;
  const basePrice = product.basePricePerKg || product.current_price || product.price || 0;
  const discountedPrice = Math.round(basePrice * (1 - bonusPercent / 100));
  const userHasInterest = shareData?.user_interest != null;
  const percent = shareData?.percent || 0;
  const totalQty = shareData?.total_quantity || 0;
  const remaining = shareData?.remaining || packSize;
  const participants = shareData?.participants || [];

  if (!isExpanded) {
    return (
      <div className="pack-share-collapsed" onClick={() => setIsExpanded(true)}>
        <Users size={16} />
        <div className="pack-share-collapsed-text">
          <span className="pack-share-collapsed-title">Купить вместе за {discountedPrice.toLocaleString('ru-RU')} ₽/{unit}</span>
          <span className="pack-share-collapsed-hint">
            <s>{Math.round(basePrice).toLocaleString('ru-RU')} ₽</s> → скидка {bonusPercent}% при сборе {packSize} {pluralizeUnit(packSize, unit)}
          </span>
        </div>
        {product.share_interest_count > 0 && (
          <span className="pack-share-badge">{product.share_interest_count} чел.</span>
        )}
      </div>
    );
  }

  return (
    <div className="pack-share-section">
      <div className="pack-share-header">
        <Users size={18} />
        <span>Совместная покупка</span>
      </div>

      <div className="pack-share-benefit">
        <Zap size={14} />
        <span>Соберите {packSize} {pluralizeUnit(packSize, unit)} вместе → цена <strong>{discountedPrice.toLocaleString('ru-RU')} ₽/{unit}</strong> вместо {Math.round(basePrice).toLocaleString('ru-RU')} ₽ (скидка {bonusPercent}%)</span>
      </div>

      {/* Quantity selector */}
      {!userHasInterest && (
        <div className="pack-share-qty">
          <span className="pack-share-qty-label">Сколько вам нужно?</span>
          <div className="pack-share-qty-row">
            <div className="pack-share-stepper">
              <button
                onClick={() => setDesiredQty(Math.max(stepQty, desiredQty - stepQty))}
                disabled={desiredQty <= stepQty}
              >
                <Minus size={16} />
              </button>
              <span>{Number.isInteger(desiredQty) ? desiredQty : desiredQty.toFixed(1)} {pluralizeUnit(desiredQty, unit)}</span>
              <button onClick={() => setDesiredQty(desiredQty + stepQty)}>
                <Plus size={16} />
              </button>
            </div>
            <div className="pack-share-qty-price">
              <span className="pack-share-qty-price-new">{Math.round(discountedPrice * desiredQty).toLocaleString('ru-RU')} ₽ *</span>
              <span className="pack-share-qty-price-old">{Math.round(basePrice * desiredQty).toLocaleString('ru-RU')} ₽</span>
            </div>
          </div>
          <span className="pack-share-disclaimer">* цена при сборе полной упаковки {packSize} {pluralizeUnit(packSize, unit)}</span>
        </div>
      )}

      {/* Participants list */}
      {participants.length > 0 && (
        <div className="pack-share-participants">
          <span className="pack-share-participants-label">Уже хотят купить:</span>
          {participants.map((p, i) => (
            <div key={i} className={`pack-share-participant ${p.is_me ? 'is-me' : ''}`}>
              <span className="pack-share-participant-name">
                {p.is_me ? 'Вы' : p.name}
              </span>
              <span className="pack-share-participant-qty">
                {Number.isInteger(p.quantity) ? p.quantity : p.quantity.toFixed(1)} {pluralizeUnit(p.quantity, unit)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Progress bar */}
      <div className="pack-share-progress">
        <div className="pack-share-progress-bar">
          <div
            className="pack-share-progress-fill"
            style={{ width: `${Math.min(100, percent)}%` }}
          />
        </div>
        <div className="pack-share-progress-text">
          Набрано: {totalQty}/{packSize} {pluralizeUnit(packSize, unit)} ({Math.round(percent)}%)
        </div>
        {remaining > 0 && (
          <div className="pack-share-remaining">
            Осталось: {Number.isInteger(remaining) ? remaining : remaining.toFixed(1)} {pluralizeUnit(remaining, unit)}
          </div>
        )}
      </div>

      {/* Login hint */}
      {showLoginHint && (
        <div className="pack-share-login-hint">
          <span>Для участия войдите в профиль</span>
          <button
            className="pack-share-login-btn"
            onClick={() => { window.location.href = '/login'; }}
          >
            Войти
          </button>
        </div>
      )}

      {/* Action buttons */}
      <div className="pack-share-actions">
        {userHasInterest ? (
          <>
            <button
              className="pack-share-btn cancel"
              onClick={handleCancel}
              disabled={isCancelling}
            >
              {isCancelling ? 'Отмена...' : 'Отменить участие'}
            </button>
            <button className="pack-share-btn share" onClick={handleShare}>
              <Share2 size={16} />
              Поделиться
            </button>
          </>
        ) : (
          <>
            <button
              className="pack-share-btn join"
              onClick={handleJoin}
              disabled={isJoining}
            >
              {isJoining ? 'Присоединяюсь...' : 'Присоединиться'}
            </button>
            <button className="pack-share-btn share" onClick={handleShare}>
              <Share2 size={16} />
            </button>
          </>
        )}
      </div>

      {!userHasInterest && (
        <div className="pack-share-invite-hint">Зовите друзей - вместе выгоднее</div>
      )}
    </div>
  );
};


/**
 * ProductDetailsModal - Bottom Sheet модальное окно с деталями товара
 *
 * Особенности:
 * - Плавный свайп за пальцем
 * - Velocity-based dismiss
 * - iOS/Material Design стиль
 * - Safe area поддержка для iPhone X+
 */
const ProductDetailsModal = ({ product, onClose, onAddToCart, onUpdateQuantity, onRemoveFromCart, cartItems = [], isOpen }) => {
  // Текущее количество в корзине (0 если нет)
  const cartQuantity = product
    ? (cartItems.find(ci => ci.id === product.id || ci.id === product.product_id)?.quantity || 0)
    : 0;
  const isInCart = cartQuantity > 0;

  // Bottom sheet hook - упрощённый API
  const {
    handlers,
    sheetRef,
    dismiss,
    handleBackdropClick
  } = useBottomSheet({
    onDismiss: onClose,
    isOpen
  });

  // A11y: ссылка на кнопку закрытия и элемент, вернувший фокус после закрытия
  const closeBtnRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  // Блокируем скролл body когда модалка открыта
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Telegram expand для fullscreen
      if (Platform.isTelegram()) {
        Platform.expand();
      }
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleClose = () => {
    Platform.hapticFeedback('light');
    dismiss();
  };

  // A11y: закрытие по Escape
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  // A11y: перевод фокуса в модалку при открытии, возврат на триггер при закрытии
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current = document.activeElement;
      // Фокус на кнопке закрытия после монтирования
      requestAnimationFrame(() => {
        if (closeBtnRef.current) closeBtnRef.current.focus();
      });
      return undefined;
    }
    // При закрытии — вернуть фокус на элемент-триггер
    if (previouslyFocusedRef.current && previouslyFocusedRef.current.focus) {
      previouslyFocusedRef.current.focus();
      previouslyFocusedRef.current = null;
    }
    return undefined;
  }, [isOpen]);

  // Добавить в корзину (только для новых товаров)
  const handleAddToCart = () => {
    Platform.hapticFeedback('success');
    onAddToCart(product, product.min_order_quantity || 1);
  };

  // +/- прямо из модалки (товар уже в корзине)
  const productId = product?.id || product?.product_id;

  const handleIncrement = () => {
    Platform.hapticFeedback('light');
    onUpdateQuantity && onUpdateQuantity(productId, cartQuantity + (product.step_quantity || 1));
  };

  const handleDecrement = () => {
    Platform.hapticFeedback('light');
    const newQty = cartQuantity - (product.step_quantity || 1);
    if (newQty <= 0) {
      onRemoveFromCart && onRemoveFromCart(productId);
    } else {
      onUpdateQuantity && onUpdateQuantity(productId, newQty);
    }
  };

  if (!isOpen || !product) return null;

  // Конфигурация типов обработки с Lucide иконками
  const processingConfig = {
    fresh: { Icon: Fish, label: 'Свежая', className: 'fresh' },
    frozen: { Icon: Snowflake, label: 'Заморозка в море', className: 'frozen' },
    salted: { Icon: Package, label: 'Слабосолёная', className: 'salted' },
    smoked: { Icon: Flame, label: 'Копчёная', className: 'smoked' },
    dried: { Icon: Thermometer, label: 'Сушёная', className: 'dried' },
    canned: { Icon: Package, label: 'Консервированная', className: 'canned' },
    pickled: { Icon: Package, label: 'Маринованная', className: 'pickled' },
    marinated: { Icon: Package, label: 'Маринованная', className: 'marinated' }
  };

  // Конфигурация типов вылова (fallback пока нет в БД)
  const catchConfig = {
    wild: { Icon: Anchor, label: 'Дикий вылов', className: 'wild' },
    farmed: { Icon: Fish, label: 'Аквакультура', className: 'farmed' }
  };

  // Конфигурация типов заморозки
  const freezingConfig = {
    ship: { Icon: Ship, label: 'Заморозка', sublabel: 'в море', className: 'ship-frozen' },
    shore: { Icon: Snowflake, label: 'Шоковая', sublabel: 'заморозка', className: 'shore-frozen' },
    chilled: { Icon: Thermometer, label: 'Охлаждённое', sublabel: '', className: 'chilled' }
  };

  // Определяем тип вылова (пока из processing_type или fallback)
  const getCatchType = () => {
    if (product.catch_type) return product.catch_type;
    // Fallback: frozen обычно = дикий вылов (замороженная в море)
    if (product.processing_type === 'frozen') return 'wild';
    // По умолчанию - дикий
    return 'wild';
  };

  // Определяем тип заморозки
  const getFreezingType = () => {
    // Если есть явное поле freezing_type - используем его
    if (product.freezing_type) return product.freezing_type;
    // Fallback на основе processing_type
    if (product.processing_type === 'frozen') return 'ship'; // Заморозка в море
    if (product.processing_type === 'fresh') return 'chilled'; // Охлаждённое
    // Для остальных (salted, smoked и т.д.) - шоковая заморозка по умолчанию
    return 'shore';
  };

  // Расчёт индивидуального бонуса за оптовую упаковку
  const calculateWholesaleBonus = () => {
    const packSize = product.wholesale_pack_size || 0;
    const bonusPercent = product.wholesale_bonus_percent || 3;
    const currentQty = cartQuantity || (product.min_order_quantity || 1);
    const progress = packSize > 0 ? Math.min(100, (currentQty / packSize) * 100) : 0;
    const remaining = packSize > 0 ? Math.max(0, packSize - currentQty) : 0;
    const isAchieved = packSize > 0 && currentQty >= packSize;
    return { progress, remaining, packSize, bonusPercent, isAchieved };
  };

  const wholesaleBonus = calculateWholesaleBonus();
  const catchType = getCatchType();
  const catchInfo = catchConfig[catchType];
  const freezingType = getFreezingType();
  const freezingInfo = freezingConfig[freezingType];
  const processingInfo = processingConfig[product.processing_type];
  const catchYear = product.catch_year || new Date().getFullYear();

  // Получаем короткое название месяца для сезона (как на Figma: "Окт")
  const getSeasonMonth = (year) => {
    // Если есть конкретный месяц вылова - используем его
    if (product.catch_month) {
      const months = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн',
                      'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
      return months[product.catch_month - 1];
    }
    // По умолчанию показываем "Сезон" если нет конкретного месяца
    return 'Сезон';
  };

  const isMobile = Platform.isMobile();

  return (
    <div
      className="bottom-sheet-backdrop entering"
      onClick={handleBackdropClick}
    >
      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        className={`bottom-sheet ${isMobile ? 'opening' : ''}`}
        style={{ maxHeight: isMobile ? '90vh' : '85vh' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-details-title"
        onClick={(e) => e.stopPropagation()}
        {...(isMobile ? handlers : {})}
      >
        {/* Кнопка закрытия (A11y: клавиатура + видимая ✕) */}
        <button
          ref={closeBtnRef}
          type="button"
          className="sheet-close-btn"
          onClick={handleClose}
          aria-label="Закрыть"
        >
          <X size={20} />
        </button>

        {/* Scrollable Content */}
        <div className="sheet-content">
          {/* Product Image с location chip */}
          <div className="sheet-product-image">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name || product.product_name}
                loading="lazy"
              />
            ) : (
              <div className="placeholder">
                <CategoryIcon category={product.category_name} size="xlarge" />
              </div>
            )}

            {/* Location Chip - top-left (место вылова) */}
            {(product.catch_region || product.country_origin) && (
              <div className="location-chip">
                <MapPin size={14} />
                <span>
                  {product.catch_region || product.country_origin}
                </span>
              </div>
            )}
          </div>

          {/* Ice Cube Feature Chips - под фото */}
          <div className="ice-chips-container">
            {/* Тип вылова */}
            {catchInfo && (
              <div className={`ice-chip ${catchInfo.className}`}>
                <div className="ice-chip-icon">
                  <catchInfo.Icon size={20} />
                </div>
                <div className="ice-chip-text">
                  <span className="ice-chip-line1">{catchInfo.label.split(' ')[0]}</span>
                  <span className="ice-chip-line2">{catchInfo.label.split(' ').slice(1).join(' ') || 'вылов'}</span>
                </div>
              </div>
            )}

            {/* Тип заморозки */}
            {freezingInfo && (
              <div className={`ice-chip ${freezingInfo.className}`}>
                <div className="ice-chip-icon">
                  <freezingInfo.Icon size={20} />
                </div>
                <div className="ice-chip-text">
                  <span className="ice-chip-line1">{freezingInfo.label}</span>
                  <span className="ice-chip-line2">{freezingInfo.sublabel}</span>
                </div>
              </div>
            )}

            {/* Сезон/год вылова */}
            <div className="ice-chip season">
              <div className="ice-chip-icon">
                <Calendar size={20} />
              </div>
              <div className="ice-chip-text">
                <span className="ice-chip-line1">{getSeasonMonth(catchYear)}</span>
                <span className="ice-chip-line2">{catchYear}</span>
              </div>
            </div>
          </div>

          {/* Wholesale Bonus Section - индивидуальный бонус за коробку */}
          {product.wholesale_pack_size > 0 && (
            <div className={`wholesale-bonus-section ${wholesaleBonus.isAchieved ? 'achieved' : ''}`}>
              <div className="wholesale-bonus-content">
                <div className="wholesale-bonus-bar-container">
                  <div
                    className="wholesale-bonus-bar"
                    style={{ width: `${wholesaleBonus.progress}%` }}
                  />
                </div>
                <div className="wholesale-bonus-badge">
                  скидка {wholesaleBonus.bonusPercent}%
                </div>
              </div>
              <div className="wholesale-bonus-text">
                {wholesaleBonus.isAchieved
                  ? <><Zap size={14} /> Скидка {wholesaleBonus.bonusPercent}% получена!</>
                  : <>Осталось {Number.isInteger(wholesaleBonus.remaining) ? wholesaleBonus.remaining : wholesaleBonus.remaining.toFixed(1)} {pluralizeUnit(wholesaleBonus.remaining, product.unit || 'кг')} для скидки</>
                }
              </div>
            </div>
          )}

          {/* Pack Sharing Section — Совместная покупка */}
          <PackShareSection
            product={product}
            pluralizeUnit={pluralizeUnit}
          />

          {/* Product Info */}
          <div className="sheet-product-info">
            {/* Title & Price */}
            <div>
              <h2 id="product-details-title" className="sheet-product-title">
                {product.name || product.product_name}
              </h2>
              <div className="sheet-product-price">
                <span className="price-value">
                  {Math.round(product.basePricePerKg || product.current_price || product.price || 0).toLocaleString('ru-RU')} ₽
                </span>
                <span className="price-unit">
                  за {accusativeUnit(product.unit || 'кг')}
                </span>
              </div>
            </div>

            {/* Badges - только покупатели (остальное на фото) */}
            {product.buyersCount > 0 && (
              <div className="sheet-badges">
                <span className="sheet-badge buyers">
                  <Award size={14} style={{ marginRight: 4 }} />
                  Уже {product.buyersCount === 1 ? 'заказал' : 'заказали'}: {product.buyersCount}
                </span>
              </div>
            )}

            {/* Описание товара (если есть) */}
            {product.description_ru && (
              <p className="sheet-product-description">{product.description_ru}</p>
            )}

            {/* Fish Journey Section - Путь рыбы */}
            <div className="fish-journey-section">
              <div className="fish-journey-header">
                <Fish size={18} />
                <span>{getJourneyTitle(product.category_name)}</span>
              </div>

              {/* Шаг 1: Вылов */}
              <div className="journey-step">
                <div className="journey-step-icon">
                  <Ship size={18} />
                </div>
                <div className="journey-step-content">
                  <div className="journey-step-title">Вылов</div>
                  <div className="journey-step-description">
                    {product.country_origin || 'Россия'}
                    {product.catch_region && `, ${product.catch_region}`}
                    {catchInfo && ` • ${catchInfo.label}`}
                  </div>
                </div>
              </div>

              {/* Шаг 2: Обработка */}
              {processingInfo && (
                <div className="journey-step">
                  <div className="journey-step-icon processing">
                    <processingInfo.Icon size={18} />
                  </div>
                  <div className="journey-step-content">
                    <div className="journey-step-title">Обработка</div>
                    <div className="journey-step-description">
                      {processingInfo.label}
                      {product.producer && ` • ${product.producer}`}
                    </div>
                  </div>
                </div>
              )}

              {/* Шаг 3: Доставка */}
              <div className="journey-step">
                <div className="journey-step-icon delivery">
                  <Truck size={18} />
                </div>
                <div className="journey-step-content">
                  <div className="journey-step-title">Доставка</div>
                  <div className="journey-step-description">
                    Прямая поставка в ваш район • Свежесть гарантирована
                  </div>
                </div>
              </div>
            </div>

            {/* Info Grid */}
            <div className="sheet-info-grid">
              {product.min_order_quantity && (
                <div className="sheet-info-item">
                  <div className="info-label">Минимальный заказ</div>
                  <div className="info-value">
                    {product.min_order_quantity} {pluralizeUnit(product.min_order_quantity, product.unit || 'кг')}
                  </div>
                </div>
              )}
              {product.wholesale_pack_size && (
                <div className="sheet-info-item">
                  <div className="info-label">Упаковка</div>
                  <div className="info-value">
                    {product.wholesale_pack_size} {pluralizeUnit(product.wholesale_pack_size, product.unit || 'кг')}
                  </div>
                </div>
              )}
              {product.shelf_life_days && (
                <div className="sheet-info-item">
                  <div className="info-label">Срок хранения</div>
                  <div className="info-value">
                    {product.shelf_life_days} дней
                  </div>
                </div>
              )}
              {(product.storage_temp_min !== undefined || product.storage_temp_max !== undefined) && (
                <div className="sheet-info-item">
                  <div className="info-label">Температура</div>
                  <div className="info-value">
                    {product.storage_temp_min}°C — {product.storage_temp_max}°C
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer with Safe Area */}
        <div className="sheet-footer">
          <div className="sheet-footer-content" style={{ justifyContent: 'center' }}>
            {isInCart ? (
              /* Товар в корзине — компактный степпер по центру */
              <div style={{
                display: 'flex', alignItems: 'center',
                gap: 0, background: '#f1f5f9', borderRadius: 10,
                overflow: 'hidden', minHeight: 44,
                width: '100%', maxWidth: 260
              }}>
                <button
                  onClick={handleDecrement}
                  style={{
                    width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: 'none', background: 'transparent', cursor: 'pointer', color: '#0EA5E9'
                  }}
                  aria-label="Уменьшить количество"
                >
                  <span style={{ fontSize: 20, fontWeight: 600 }}>−</span>
                </button>
                <span style={{
                  flex: 1, textAlign: 'center', fontSize: 16, fontWeight: 700,
                  color: '#1e293b', userSelect: 'none'
                }}>
                  {cartQuantity} {pluralizeUnit(cartQuantity, product.unit || 'кг')}
                </span>
                <button
                  onClick={handleIncrement}
                  style={{
                    width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: 'none', background: 'transparent', cursor: 'pointer', color: '#0EA5E9'
                  }}
                  aria-label="Увеличить количество"
                >
                  <span style={{ fontSize: 20, fontWeight: 600 }}>+</span>
                </button>
              </div>
            ) : (
              /* Новый товар — кнопка "В корзину" по центру */
              <button
                className="sheet-add-to-cart"
                onClick={handleAddToCart}
                style={{ width: '100%', maxWidth: 320 }}
              >
                В корзину • <span className="price">
                  {Math.round((product.basePricePerKg || product.current_price || product.price || 0) * (product.min_order_quantity || 1)).toLocaleString('ru-RU')} ₽
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsModal;
